// Shared queue table model: the configurable column set, per-column cell
// renderers, sort values, and a clickable sortable <SortHeader>. Used by both
// the per-portal queue and the unified Service Desk queue so they stay in sync.
import React from 'react';
import { StatusBadge, PriorityBadge, CategoryBadge } from './ticketMeta';
import SlaBadge from './SlaBadge';

const PRIORITY_ORDER = { urgent: 4, high: 3, medium: 2, low: 1 };
const STATUS_ORDER = { open: 1, in_progress: 2, pending: 3, resolved: 4, closed: 5 };

export const ALL_QUEUE_COLUMNS = [
  { key: 'key', label: 'Key', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'category', label: 'Type', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'assignee', label: 'Assignee', sortable: true },
  { key: 'requester', label: 'Requester', sortable: true },
  { key: 'sla', label: 'SLA', sortable: true },
  { key: 'created', label: 'Created', sortable: true },
  { key: 'updated', label: 'Updated', sortable: true },
];
export const QUEUE_COLUMN_LABELS = Object.fromEntries(ALL_QUEUE_COLUMNS.map((c) => [c.key, c.label]));
export const DEFAULT_QUEUE_COLUMNS = ['key', 'title', 'status', 'priority', 'sla', 'created'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');

export function columnSortValue(t, key, ctx = {}) {
  switch (key) {
    case 'key': {
      // Natural sort: "AMC-2" must come before "AMC-10". Zero-pad the trailing
      // number so a plain string compare orders by prefix then numeric value.
      const k = t.ticket_key || '';
      const m = /^(.*?)(\d+)\s*$/.exec(k);
      return m ? `${m[1]}${m[2].padStart(12, '0')}` : k;
    }
    case 'title': return (t.title || '').toLowerCase();
    case 'category': return t.category || '';
    case 'status': return STATUS_ORDER[t.status] || 99;
    case 'priority': return PRIORITY_ORDER[t.priority] || 0;
    case 'assignee': return (ctx.memberById?.[t.assignee_user_id]?.name || '~').toLowerCase();
    case 'requester': return (t.requester_name || t.requester_email || '~').toLowerCase();
    case 'created': return new Date(t.created_at || 0).getTime();
    case 'updated': return new Date(t.updated_at || t.created_at || 0).getTime();
    case 'sla': return t.sla_state?.resolution?.remaining_mins ?? 1e12;
    case 'portal': return (ctx.portalById?.[t.portal_id]?.name || '').toLowerCase();
    default: return '';
  }
}

export function ColumnCell({ col, t, ctx = {} }) {
  switch (col) {
    case 'key': return <span className="font-mono text-xs text-blue-700 font-semibold">{t.ticket_key}</span>;
    case 'title': return <span className="text-zinc-900 font-medium">{t.title}</span>;
    case 'category': return <CategoryBadge category={t.category} />;
    case 'status': return <StatusBadge status={t.status} />;
    case 'priority': return <PriorityBadge priority={t.priority} />;
    case 'assignee': {
      const a = ctx.memberById?.[t.assignee_user_id];
      if (!a) return <span className="text-zinc-400 text-xs">Unassigned</span>;
      const ini = (a.name || a.email || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
      return (
        <span className="inline-flex items-center gap-1.5 text-zinc-600 text-xs">
          {a.avatar
            ? <img src={a.avatar} alt="" className="w-[22px] h-[22px] rounded-full object-cover ring-1 ring-zinc-200" />
            : <span className="w-[22px] h-[22px] rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 text-zinc-600 text-[9px] font-bold flex items-center justify-center">{ini}</span>}
          {a.name || a.email}
        </span>
      );
    }
    case 'requester': return <span className="text-zinc-600 text-xs">{t.requester_name || t.requester_email || '—'}</span>;
    case 'sla': return <SlaBadge slaState={t.sla_state} />;
    case 'created': return <span className="text-zinc-500 text-xs">{fmtDate(t.created_at)}</span>;
    case 'updated': return <span className="text-zinc-500 text-xs">{fmtDate(t.updated_at || t.created_at)}</span>;
    case 'portal': return <span className="text-zinc-600 text-xs">{ctx.portalById?.[t.portal_id]?.name || '—'}</span>;
    default: return null;
  }
}

export function SortHeader({ col, label, sortBy, sortDir, onSort, sortable = true, className = '', style }) {
  const active = sortBy === col;
  return (
    <th style={style} className={`text-left py-2.5 px-4 font-semibold select-none whitespace-nowrap ${className}`}>
      {sortable ? (
        <button onClick={() => onSort(col)} className="inline-flex items-center gap-1 hover:text-zinc-800 transition-colors" data-testid={`sd-sort-${col}`}>
          {label}
          <span className={`text-[9px] ${active ? 'text-zinc-800' : 'text-zinc-300'}`}>{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
        </button>
      ) : label}
    </th>
  );
}

export function sortTickets(list, sortBy, sortDir, ctx) {
  if (!sortBy) return list;
  const sorted = [...list].sort((a, b) => {
    const av = columnSortValue(a, sortBy, ctx);
    const bv = columnSortValue(b, sortBy, ctx);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
  });
  return sortDir === 'desc' ? sorted.reverse() : sorted;
}
